import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { useTrainingSession } from './session';
import { RegisterGate } from './RegisterGate';
import { findModule, totalCurriculumDuration } from './curriculum';

export function ModuleDetail() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const { persona, moduleProgress } = useTrainingSession();
  const navigate = useNavigate();
  const mod = moduleId ? findModule(moduleId) : undefined;

  const isLocked = mod?.access === 'pro' && persona !== 'pro-learner' && persona !== 'manager' && persona !== 'anonymous';

  useEffect(() => {
    if (mod && isLocked) {
      trackEvent('locked_module_viewed', { module_id: mod.id, from: 'module_detail' });
    }
  }, [mod, isLocked]);

  if (persona === 'anonymous') {
    return (
      <div className="tr-root">
        <div className="tr-wrap tr-appsec">
          <RegisterGate />
        </div>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="tr-root">
        <div className="tr-wrap tr-appsec">
          <p>That module doesn't exist.</p>
          <Link to="/training/catalog">Back to all modules</Link>
        </div>
      </div>
    );
  }

  const progress = moduleProgress[mod.id];

  return (
    <div className="tr-root">
      <div className="tr-wrap tr-appsec">
        <button className="tr-back" onClick={() => navigate('/training/catalog')}>
          <i className="ti ti-arrow-left"></i> All modules
        </button>
        <div className="tr-mod-head">
          <h1>{mod.title}</h1>
          {mod.access === 'free' ? (
            <span className="tr-badge tr-badge-free">Free</span>
          ) : (
            <span className="tr-badge tr-badge-pro">{isLocked && <i className="ti ti-lock"></i>}Pro</span>
          )}
        </div>
        <div className="tr-meta" style={{ marginTop: 16 }}>
          <span><i className="ti ti-clock"></i>{mod.durationLabel}</span>
          <span><i className="ti ti-book"></i>{mod.lessons.length} lessons</span>
          <span>Module {mod.number} of 8</span>
        </div>
        <p className="tr-mod-desc">{mod.description}</p>

        {!isLocked && progress && (
          <div style={{ marginTop: 24, maxWidth: 420 }}>
            <div className="tr-bar">
              <i style={{ width: `${(progress.completedLessons / progress.totalLessons) * 100}%` }} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--tr-muted)', marginTop: 7 }}>
              {progress.completedLessons} of {progress.totalLessons} lessons done
            </div>
          </div>
        )}

        <ul className="tr-llist">
          {mod.lessons.map((lesson, i) => {
            const lessonNum = i + 1;
            const done = !isLocked && progress && lessonNum <= progress.completedLessons;
            const current = !isLocked && progress && lessonNum === progress.completedLessons + 1;
            return (
              <li key={lesson.id} className={isLocked ? 'tr-off' : done ? 'tr-done' : current ? 'tr-cur' : ''}>
                <span className={`tr-lic${done ? ' tr-ok' : current ? ' tr-go' : ''}`}>
                  {isLocked ? <i className="ti ti-lock"></i> : done ? <i className="ti ti-check"></i> : current ? <i className="ti ti-player-play"></i> : lessonNum}
                </span>
                <span className="tr-lt">{lesson.title}</span>
                <span className="tr-ldur">{lesson.durationMin} min</span>
              </li>
            );
          })}
        </ul>

        {isLocked ? (
          <div className="tr-paywall">
            <div className="tr-paywall-h">
              <i className="ti ti-lock"></i>
              <h3>This module comes with Pro</h3>
            </div>
            <div className="tr-paywall-b">
              <p>
                Module 1 stays free, permanently. Pro opens the remaining
                seven for everyone on your team — it's bought once, by
                whoever runs the team.
              </p>
              <div className="tr-pw-grid">
                <div><i className="ti ti-check"></i>All eight modules, {totalCurriculumDuration()}</div>
                <div><i className="ti ti-check"></i>New modules as they're released</div>
                <div><i className="ti ti-check"></i>Assign modules with due dates</div>
                <div><i className="ti ti-check"></i>Exportable completion records</div>
              </div>
              <div className="tr-pw-foot">
                <Link
                  className="tr-btn tr-btn-primary"
                  to="/training/plans"
                  onClick={() => trackEvent('upgrade_panel_viewed', { source: 'module_detail', module_id: mod.id })}
                >
                  See what's in Pro <i className="ti ti-arrow-right"></i>
                </Link>
                <button
                  className="tr-btn tr-btn-ghost"
                  onClick={() => trackEvent('upgrade_cta_clicked', { source: 'send_to_manager', module_id: mod.id })}
                >
                  <i className="ti ti-send"></i> Send this to your manager
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 26 }}>
            {(() => {
              const completed = progress?.completedLessons ?? 0;
              const isComplete = progress !== undefined && completed === progress.totalLessons;
              const nextLesson = isComplete ? 1 : completed + 1;
              const label = isComplete
                ? 'Review module'
                : completed > 0
                  ? `Continue lesson ${nextLesson}`
                  : 'Start module';
              return (
                <Link
                  className="tr-btn tr-btn-primary"
                  to={`/training/module/${mod.id}/lesson/${nextLesson}`}
                  onClick={() => trackEvent('module_started', { module_id: mod.id })}
                >
                  {label} <i className="ti ti-arrow-right"></i>
                </Link>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
